import { action, makeObservable } from 'mobx'

const OPEN_EVENT_NAME = 'open'
const CLOSED_EVENT_NAME = 'close'
const ANIMATION_STEP = 20

export default class ElementSwiper {
  public isOpened: boolean = false
  private element: HTMLElement | null
  private direction: 'left' | 'right'

  private scrollMax: number
  private touchStartX: number
  private touchStartY: number
  private scrollStart: number
  private touchDeltaX: number
  private touchDeltaY: number
  private touchAngle: number
  private wasCanceling: boolean
  private moveStart: number
  private moveStartDelta: number

  public skipOpenAnimation = false
  private handlers: { [action: string]: Array<() => void> } = {}

  public constructor(
    element: HTMLElement,
    options?: { direction?: 'left' | 'right' },
  ) {
    this.element = element
    this.direction = options?.direction || 'left'
    this.scrollMax = element.scrollWidth - element.clientWidth

    if (this.direction === 'right') {
      element.scrollLeft = this.scrollMax
    }

    element.addEventListener('touchstart', this.onTouchStart, { passive: true })

    element.addEventListener('touchmove', this.onTouchMove, { passive: true })

    element.addEventListener('touchend', this.onTouchEnd)
    makeObservable(this)
  }

  public disconnectElement() {
    if (!this.element) return

    this.element.removeEventListener('touchstart', this.onTouchStart)
    this.element.removeEventListener('touchmove', this.onTouchMove)
    this.element.removeEventListener('touchend', this.onTouchEnd)

    this.handlers = {}
    this.element = null
  }

  public get containerElement(): HTMLElement | null {
    return this.element
  }

  public animateClosed = (step = ANIMATION_STEP) => {
    if (!this.element) return

    if (this.direction === 'right') {
      const frameRequestCb = () => {
        this.element.scrollLeft = Math.min(
          this.element.scrollLeft + step,
          this.scrollMax,
        )
        if (this.element.scrollLeft < this.scrollMax) {
          window.requestAnimationFrame(frameRequestCb)
          return
        }
        this.setClosedStatus()
      }
      window.requestAnimationFrame(frameRequestCb)
    } else {
      const frameRequestCb = () => {
        this.element.scrollLeft = Math.max(this.element.scrollLeft - step, 0)
        if (this.element.scrollLeft > 0) {
          window.requestAnimationFrame(frameRequestCb)
          return
        }
        this.setClosedStatus()
      }
      window.requestAnimationFrame(frameRequestCb)
    }
  }

  public animateOpen = (step = ANIMATION_STEP) => {
    if (!this.element) {
      return // Warning: kludge
    }

    if (this.skipOpenAnimation) {
      if (this.direction === 'right') {
        this.element.scrollLeft = 0
      } else {
        this.element.scrollLeft =
          this.element.scrollWidth - this.element.clientWidth
      }
      this.setOpenedStatus()
      return
    }

    if (this.direction === 'right') {
      const frameRequestCb = () => {
        if (!this.element) {
          return // Warning: kludge
        }
        this.element.scrollLeft = Math.max(this.element.scrollLeft - step, 0)
        if (this.element.scrollLeft > 0) {
          window.requestAnimationFrame(frameRequestCb)
          return
        }
        this.element.scrollLeft = 0
        this.setOpenedStatus()
      }
      window.requestAnimationFrame(frameRequestCb)
    } else {
      const clamp = this.element.scrollWidth - this.element.clientWidth
      const frameRequestCb = () => {
        if (!this.element) {
          return // Warning: kludge
        }
        this.element.scrollLeft = Math.min(
          clamp,
          this.element.scrollLeft + step,
        )
        if (this.element.scrollLeft < clamp) {
          window.requestAnimationFrame(frameRequestCb)
          return
        }
        this.element.scrollLeft = clamp
        this.setOpenedStatus()
      }
      window.requestAnimationFrame(frameRequestCb)
    }
  }

  public on(event: 'open' | 'close', handler: () => void) {
    if (!this.handlers[event]) {
      this.handlers[event] = []
    }
    this.handlers[event].push(handler)
  }

  @action
  private onTouchStart = (e: TouchEvent) => {
    this.touchStartX = e.touches[0].clientX
    this.touchStartY = e.touches[0].clientY
    this.scrollStart = this.element.scrollLeft
  }

  @action
  private onTouchMove = (e: TouchEvent) => {
    this.touchDeltaX = this.touchStartX - e.touches[0].clientX
    this.touchDeltaY = this.touchStartY - e.touches[0].clientY
    this.touchAngle =
      (Math.atan(this.touchDeltaY / this.touchDeltaX) * 180) / Math.PI +
      (this.touchDeltaX < 0 ? 180 : this.touchDeltaY < 0 ? 360 : 0)

    if (this._isVertical(this.touchAngle, 45)) {
      this.wasCanceling = true
      return
    } else if (this.wasCanceling || this.moveStart === undefined) {
      this.wasCanceling = false
      this.moveStart = e.touches[0].clientX
    }

    this.moveStartDelta = this.moveStart - e.touches[0].clientX

    const { min, max } = Math
    this.element.scrollLeft = min(
      this.scrollMax,
      max(this.scrollStart + this.moveStartDelta, 0),
    )
  }

  @action
  private onTouchEnd = () => {
    if (this.direction === 'right') {
      switch (this.element.scrollLeft) {
        case this.scrollMax:
          this.setClosedStatus()
          return
        case 0:
          this.setOpenedStatus()
          return
      }

      const middlePoint = this.scrollMax / 2

      if (this.element.scrollLeft <= middlePoint) {
        this.animateOpen()
      } else {
        this.animateClosed()
      }
    } else {
      switch (this.element.scrollLeft) {
        case 0:
          this.setClosedStatus()
          return
        case this.scrollMax:
          this.setOpenedStatus()
          return
      }

      const middlePoint = this.scrollMax / 2

      if (this.element.scrollLeft >= middlePoint) {
        this.animateOpen()
      } else {
        this.animateClosed()
      }
    }
  }

  private _isVertical(angle: number, maxAngleDelta: number): boolean {
    return !!(
      Math.abs(270 - angle) <= maxAngleDelta ||
      Math.abs(90 - angle) <= maxAngleDelta
    )
  }

  @action.bound
  private setOpenedStatus() {
    this.isOpened = true
    if (this.handlers[OPEN_EVENT_NAME]) {
      this.handlers[OPEN_EVENT_NAME].forEach(handler => {
        handler()
      })
    }
    this.moveStart = undefined
  }

  @action.bound
  private setClosedStatus() {
    this.isOpened = false
    if (this.handlers[CLOSED_EVENT_NAME]) {
      this.handlers[CLOSED_EVENT_NAME].forEach(handler => {
        handler()
      })
    }
    this.moveStart = undefined
  }
}
